import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, MapPin, AlertTriangle, Music, Compass, DollarSign, Users, Star, Moon, Sparkles, CheckCircle, PartyPopper as Party, FileLock as Cocktail, Camera, Heart, ChevronRight, Shield, Ticket, Mail } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import NightlifeBookingForm from '../components/NightlifeBookingForm';
import NightCrawlingGuideModal from '../components/NightCrawlingGuideModal';
import NightlifeTermsModal from '../components/NightlifeTermsModal';

const UgandaNightlifeGuide = () => {
  const [selectedPackage, setSelectedPackage] = useState<any | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [selectedTermsPackage, setSelectedTermsPackage] = useState<'kampala' | 'jinja' | 'premium'>('kampala');
  const [includePhotoPackage, setIncludePhotoPackage] = useState(false);

  const nightlifePackages = [
    {
      id: 'kampala',
      title: 'Kampala Night Crawling',
      image: 'https://images.unsplash.com/photo-1566737236500-c8ac43014a67?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80',
      price: 30,
      duration: '6 hours',
      highlights: [
        'Visit 3 top nightclubs',
        'Street food tasting',
        'VIP entry to all venues',
        'Dedicated guide & security'
      ],
      description: 'Experience Kampala\'s vibrant nightlife with VIP access to the hottest clubs, delicious street food, and expert local guides to show you the best spots.'
    },
    {
      id: 'jinja',
      title: 'Jinja Night Crawling',
      image: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80',
      price: 30,
      duration: '5 hours',
      highlights: [
        'Riverside bar hopping',
        'Local craft beer tasting',
        'Live music venues',
        'Nighttime boat cruise'
      ],
      description: 'Discover Jinja\'s unique nightlife scene along the Nile River with exclusive access to riverside bars, live music venues, and a magical nighttime boat cruise.'
    },
    {
      id: 'premium',
      title: 'Premium VIP Experience',
      image: 'https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1829&q=80',
      price: 100,
      duration: '8 hours',
      highlights: [
        'Bottle service included',
        'Private transportation',
        'Professional photographer',
        'Exclusive VIP areas'
      ],
      description: 'Our premium package includes everything you need for an unforgettable night: bottle service, private transportation, a professional photographer, and access to exclusive VIP areas.'
    }
  ];

  const handleBookNow = (pkg: any) => {
    setSelectedPackage(pkg);
    setIsBookingModalOpen(true);
  };

  const handleAddPhotoPackage = () => {
    setIncludePhotoPackage(true);
    setSelectedPackage(nightlifePackages[0]); // Default to first package
    setIsBookingModalOpen(true);
  };

  const handleOpenTerms = (packageType: 'kampala' | 'jinja' | 'premium') => {
    setSelectedTermsPackage(packageType);
    setIsTermsModalOpen(true);
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2670&q=80"
            alt="Uganda Nightlife"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
          
          {/* Animated party effects */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-purple-500 rounded-full animate-ping" style={{ animationDuration: '3s', animationDelay: '0.5s' }}></div>
            <div className="absolute top-1/3 left-1/2 w-2 h-2 bg-blue-500 rounded-full animate-ping" style={{ animationDuration: '2.5s', animationDelay: '0.2s' }}></div>
            <div className="absolute top-1/2 left-1/3 w-2 h-2 bg-pink-500 rounded-full animate-ping" style={{ animationDuration: '4s', animationDelay: '1s' }}></div>
            <div className="absolute top-2/3 left-2/3 w-2 h-2 bg-green-500 rounded-full animate-ping" style={{ animationDuration: '3.5s', animationDelay: '0.7s' }}></div>
            <div className="absolute top-3/4 left-1/4 w-2 h-2 bg-yellow-500 rounded-full animate-ping" style={{ animationDuration: '2.8s', animationDelay: '1.2s' }}></div>
          </div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="inline-flex items-center bg-purple-500/20 rounded-full px-2.5 py-0.5 text-xs text-purple-400 mb-4 backdrop-blur-sm">
              <Moon className="h-3 w-3 mr-1" />
              Nightlife Guide
            </div>
            <h1 className="text-4xl font-bold mb-4">Uganda Night Crawling & Club Guide</h1>
            <p className="text-lg text-white/90 mb-6">
              Your ultimate guide to exploring Uganda's vibrant nightlife scene, from exclusive clubs to hidden speakeasies and late-night street food. Book with us for a safe, all-inclusive experience!
            </p>
            
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#packages" className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1 duration-300">
                View Nightlife Packages
              </a>
              <button 
                onClick={() => setIsGuideModalOpen(true)}
                className="px-6 py-3 bg-white/10 backdrop-blur-sm text-white border border-white/30 rounded-lg hover:bg-white/20 transition-colors"
              >
                Explore Club Guides
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Packages */}
      <section id="packages" className="py-16 bg-gradient-to-b from-purple-900 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">All-Inclusive Nightlife Packages</h2>
            <p className="text-lg text-white/80 max-w-3xl mx-auto">
              Book your ultimate night out with our all-inclusive packages. Once payment is made, everything is on us - from street food to drinks and club entries!
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {nightlifePackages.map((pkg) => (
              <div 
                key={pkg.id}
                className="bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden border border-white/20 hover:border-purple-500/50 transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="relative h-48">
                  <img 
                    src={pkg.image}
                    alt={pkg.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <h3 className="text-xl font-bold text-white">{pkg.title}</h3>
                    <div className="flex items-center mt-1">
                      <Clock className="h-4 w-4 text-purple-400 mr-1" />
                      <span className="text-white/80 text-sm">{pkg.duration}</span>
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div className="text-2xl font-bold text-white">${pkg.price}</div>
                    <div className="text-sm text-white/60">per person</div>
                  </div>
                  
                  <p className="text-white/80 mb-4">{pkg.description}</p>
                  
                  <div className="space-y-2 mb-6">
                    {pkg.highlights.map((highlight, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-white/80">{highlight}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    <button 
                      onClick={() => handleBookNow(pkg)}
                      className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
                    >
                      <Ticket className="h-5 w-5 mr-2" />
                      Book Now
                    </button>
                    
                    <button 
                      onClick={() => handleOpenTerms(pkg.id as 'kampala' | 'jinja' | 'premium')}
                      className="text-center text-white/70 text-sm hover:text-white transition-colors mt-2"
                    >
                      Terms & Conditions
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="md:w-2/3">
                <h3 className="text-2xl font-bold mb-4">Group Booking Benefits</h3>
                <p className="text-white/80 mb-4">
                  Booking as a group? Enjoy special benefits and discounts when you book for 4 or more people!
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">15% discount for groups of 4-6 people</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">20% discount for groups of 7+ people</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">Complimentary bottle of champagne for groups of 10+</span>
                  </li>
                </ul>
              </div>
              <div className="md:w-1/3 bg-purple-900/50 p-6 rounded-xl">
                <h4 className="text-xl font-bold mb-3">Contact for Group Bookings</h4>
                <p className="text-white/80 mb-4">
                  For groups of 4 or more, contact our team for special arrangements and discounts.
                </p>
                <a 
                  href="mailto:nightlife@travelcord.guide" 
                  className="inline-flex items-center px-4 py-2 bg-white text-purple-900 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info Section */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Night Crawling Essentials</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <Calendar className="h-5 w-5 text-purple-600 mr-2" />
                <h3 className="font-medium text-gray-900">Best Nights</h3>
              </div>
              <p className="text-gray-600">Thursday-Saturday</p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <Clock className="h-5 w-5 text-purple-600 mr-2" />
                <h3 className="font-medium text-gray-900">Peak Hours</h3>
              </div>
              <p className="text-gray-600">11PM-3AM</p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <MapPin className="h-5 w-5 text-purple-600 mr-2" />
                <h3 className="font-medium text-gray-900">Starting Point</h3>
              </div>
              <p className="text-gray-600">Fountain Square</p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <Users className="h-5 w-5 text-purple-600 mr-2" />
                <h3 className="font-medium text-gray-900">Dress Code</h3>
              </div>
              <p className="text-gray-600">Upscale casual</p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">What's Included in Your Package</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Ticket className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">VIP Club Entry</h3>
              <p className="text-gray-600">
                Skip the lines with VIP access to all clubs and venues. No cover charges or entry fees - it's all included!
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Cocktail className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Food & Drinks</h3>
              <p className="text-gray-600">
                Enjoy complimentary street food tastings and welcome drinks at each venue. Additional drink options available.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Safety & Guidance</h3>
              <p className="text-gray-600">
                Professional guides and security personnel ensure your safety throughout the night while showing you the best spots.
              </p>
            </div>
          </div>
          
          <div className="mt-12 bg-white p-8 rounded-lg shadow-sm border border-gray-200">
            <div className="flex flex-col md:flex-row items-start gap-8">
              <div className="md:w-1/2">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Why Book With Us</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">All-inclusive packages with no hidden fees</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Local experts who know the best spots and hidden gems</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Priority access and VIP treatment at all venues</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Safe and reliable transportation between venues</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">24/7 emergency support during your night out</span>
                  </li>
                </ul>
              </div>
              
              <div className="md:w-1/2 bg-purple-50 p-6 rounded-lg">
                <div className="flex items-center mb-4">
                  <Camera className="h-6 w-6 text-purple-600 mr-2" />
                  <h3 className="text-xl font-semibold text-gray-900">Photo Package Add-on</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Capture your night with our professional photography add-on. A dedicated photographer will join your group to document your experience.
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-purple-600 font-semibold">$30 per group</span>
                  <button 
                    onClick={handleAddPhotoPackage}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                  >
                    Add to Booking
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Street Food Circuit */}
      <section id="food" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Street Food Circuit</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Spicy Eel Skewers</h3>
                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">Hot 🔥</span>
              </div>
              <p className="text-gray-600 mb-4">Delicious marinated eel skewers grilled to perfection with a spicy sauce that packs a punch.</p>
              <div className="flex items-center text-sm text-gray-500">
                <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                <span>Mama Lan's Cart, Midnight Bazaar</span>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Dragonfruit Cocktails</h3>
                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">Refreshing 🍹</span>
              </div>
              <p className="text-gray-600 mb-4">Vibrant and Instagram-worthy cocktails made with fresh dragonfruit and local spirits.</p>
              <div className="flex items-center text-sm text-gray-500">
                <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                <span>Blue Lantern Stand</span>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Mooncake Madness</h3>
                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">Seasonal 🌙</span>
              </div>
              <p className="text-gray-600 mb-4">Traditional mooncakes with modern twists - try the chocolate lava or matcha flavors.</p>
              <div className="flex items-center text-sm text-gray-500">
                <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                <span>3rd Street vendors</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Night Markets & Bazaars */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Night Markets & Bazaars</h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Specialty</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">Midnight Bazaar</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">10PM-4AM</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">Local crafts, antiques</div>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">Neon Alley</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">9PM-5AM</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">Tech gadgets, glow art</div>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">Spice Lane</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">8PM-2AM</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">Rare spices, teas</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Nightclub Scene */}
      <section id="clubs" className="py-16 bg-gradient-to-b from-gray-900 to-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl font-bold mb-2">Nightclubbing Scene</h2>
              <p className="text-white/80">Uganda's Famous 3-Tier System</p>
            </div>
            <button 
              onClick={() => setIsGuideModalOpen(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center"
            >
              <Users className="h-4 w-4 mr-2" />
              View Our Guides
            </button>
          </div>
          
          <div className="space-y-12">
            {/* Platinum Tier */}
            <div>
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center mr-4 shadow-lg shadow-yellow-400/20">
                  <span className="text-gray-900 font-bold text-xl">P</span>
                </div>
                <h3 className="text-2xl font-bold">Platinum Tier Clubs</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:border-purple-500/50 transition-all duration-300">
                  <h4 className="text-xl font-bold mb-4">Eclipse</h4>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <Sparkles className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80">Rooftop infinity pool with DJs</span>
                    </li>
                    <li className="flex items-start">
                      <Users className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80">Door Policy: Strict (reservations recommended)</span>
                    </li>
                    <li className="flex items-start">
                      <DollarSign className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80">Cover: $50-100 (includes 1 drink)</span>
                    </li>
                  </ul>
                  <div className="text-white/60 text-sm">
                    <p>Included in our Premium VIP Experience package</p>
                  </div>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:border-purple-500/50 transition-all duration-300">
                  <h4 className="text-xl font-bold mb-4">Vertigo</h4>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <Sparkles className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80">57th floor views with glass dance floor</span>
                    </li>
                    <li className="flex items-start">
                      <Users className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80">Dress Code: Fashion forward (no sneakers)</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80">Known for celebrity sightings</span>
                    </li>
                  </ul>
                  <div className="text-white/60 text-sm">
                    <p>Included in our Premium VIP Experience package</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Gold Tier */}
            <div>
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-yellow-400/80 rounded-full flex items-center justify-center mr-4 shadow-lg shadow-yellow-400/20">
                  <span className="text-gray-900 font-bold text-xl">G</span>
                </div>
                <h3 className="text-2xl font-bold">Gold Tier Clubs</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:border-purple-500/50 transition-all duration-300">
                  <h4 className="text-xl font-bold mb-4">Boombox</h4>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <Music className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80">90s retro vibe with karaoke rooms</span>
                    </li>
                    <li className="flex items-start">
                      <DollarSign className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80">Cover: $20 (free before 11PM)</span>
                    </li>
                  </ul>
                  <div className="text-white/60 text-sm">
                    <p>Included in all our nightlife packages</p>
                  </div>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:border-purple-500/50 transition-all duration-300">
                  <h4 className="text-xl font-bold mb-4">Labyrinth</h4>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <Compass className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80">Maze-like layout with different music in each section</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-purple-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80">Known for innovative cocktails</span>
                    </li>
                  </ul>
                  <div className="text-white/60 text-sm">
                    <p>Included in all our nightlife packages</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Underground Scene */}
            <div>
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-yellow-400/60 rounded-full flex items-center justify-center mr-4 shadow-lg shadow-yellow-400/20">
                  <span className="text-gray-900 font-bold text-xl">U</span>
                </div>
                <h3 className="text-2xl font-bold">Underground Scene</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:border-purple-500/50 transition-all duration-300">
                  <h4 className="text-xl font-bold mb-4">The Honeycomb</h4>
                  <p className="text-white/80 mb-4">Speakeasy hidden behind a noodle shop</p>
                  <div className="flex items-center text-white/60">
                    <Star className="h-4 w-4 text-yellow-400 mr-1" />
                    <span>Known for craft cocktails and intimate atmosphere</span>
                  </div>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:border-purple-500/50 transition-all duration-300">
                  <h4 className="text-xl font-bold mb-4">Jazz Crypt</h4>
                  <p className="text-white/80 mb-4">Live music venue in a former bank vault</p>
                  <div className="flex items-center text-white/60">
                    <Music className="h-4 w-4 text-purple-400 mr-1" />
                    <span>Features local and international jazz artists</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safety & Etiquette */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Safety & Etiquette</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center mb-4">
                <Shield className="h-6 w-6 text-purple-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Your Safety is Our Priority</h3>
              </div>
              <p className="text-gray-600 mb-4">
                When you book with TravelCord, your safety is our top concern. All our nightlife packages include:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Professional guides who stay with your group</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Secure transportation between venues</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">24/7 emergency support line</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Vetted and trusted venues only</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Club Entry Tips</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Groups &gt;4 often get denied (unless with our guides!)</span>
                </li>
                <li className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">International IDs usually accepted</span>
                </li>
                <li className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">No photos in platinum clubs</span>
                </li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Transport</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Our packages include safe transportation</span>
                </li>
                <li className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Official 'Night Rider' buses run until 5AM</span>
                </li>
                <li className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Avoid unmarked taxis</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="mt-8 bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pro Tips from Our Guides</h3>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <p className="text-gray-600 italic">
                  "Buy bottle service early - clubs raise prices after 1AM. With our packages, we secure the best rates for you!"
                </p>
                <p className="text-sm text-gray-500 mt-2">- Azel, TravelCord Nightlife Guide</p>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <p className="text-gray-600 italic">
                  "The 'secret' entrance to Eclipse is through the hotel lobby after 2AM. Our guides know all these tricks to enhance your experience!"
                </p>
                <p className="text-sm text-gray-500 mt-2">- Emmy, TravelCord Nightlife Expert</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Weekly Highlights */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Weekly Highlights</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-purple-800 font-bold">W</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Wednesdays</h3>
              </div>
              <p className="text-gray-600">Ladies Night at Vertigo</p>
              <div className="mt-4 flex items-center text-sm text-gray-500">
                <Clock className="h-4 w-4 text-gray-400 mr-1" />
                <span>9PM - 3AM</span>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-purple-800 font-bold">F</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Fridays</h3>
              </div>
              <p className="text-gray-600">EDM Festival at Boombox</p>
              <div className="mt-4 flex items-center text-sm text-gray-500">
                <Clock className="h-4 w-4 text-gray-400 mr-1" />
                <span>10PM - 4AM</span>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-purple-800 font-bold">S</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Sundays</h3>
              </div>
              <p className="text-gray-600">Afterhours at The Honeycomb</p>
              <div className="mt-4 flex items-center text-sm text-gray-500">
                <Clock className="h-4 w-4 text-gray-400 mr-1" />
                <span>12AM - 6AM</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">What's included in the package price?</h3>
              <p className="text-gray-600">
                All our packages include guided nightlife tours, VIP entry to clubs, welcome drinks, street food tastings, and transportation between venues. Everything is covered once you book!
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Is it safe to go clubbing in Uganda?</h3>
              <p className="text-gray-600">
                Yes, especially with our guided tours. Our professional guides and security personnel ensure your safety throughout the night, and we only visit trusted venues.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Can I book for a large group?</h3>
              <p className="text-gray-600">
                Absolutely! We offer special discounts for groups of 4 or more. For groups larger than 10, please contact us directly for customized arrangements.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">What should I wear?</h3>
              <p className="text-gray-600">
                We recommend upscale casual attire. For platinum clubs, smart dress is required (no sneakers or t-shirts). Your guide will advise on specific dress codes for each venue.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-purple-900 text-white relative overflow-hidden">
        {/* Animated party effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-purple-500 rounded-full animate-ping" style={{ animationDuration: '3s', animationDelay: '0.5s' }}></div>
          <div className="absolute top-1/3 left-1/2 w-3 h-3 bg-blue-500 rounded-full animate-ping" style={{ animationDuration: '2.5s', animationDelay: '0.2s' }}></div>
          <div className="absolute top-1/2 left-1/3 w-3 h-3 bg-pink-500 rounded-full animate-ping" style={{ animationDuration: '4s', animationDelay: '1s' }}></div>
          <div className="absolute top-2/3 left-2/3 w-3 h-3 bg-green-500 rounded-full animate-ping" style={{ animationDuration: '3.5s', animationDelay: '0.7s' }}></div>
          <div className="absolute top-3/4 left-1/4 w-3 h-3 bg-yellow-500 rounded-full animate-ping" style={{ animationDuration: '2.8s', animationDelay: '1.2s' }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Experience Uganda's Nightlife?</h2>
          <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
            Book your all-inclusive nightlife package today and let us handle everything while you enjoy an unforgettable night out!
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="#packages" 
              className="px-8 py-4 bg-white text-purple-900 rounded-lg hover:bg-purple-100 transition-colors shadow-xl hover:shadow-2xl transform hover:-translate-y-1 duration-300 font-bold"
            >
              View Packages
            </a>
            <a 
              href="mailto:nightlife@travelcord.guide" 
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg hover:bg-white/10 transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="py-8 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-500">
            Last updated: 2025-04-11 | Contributor: @Travelcord.guide
          </p>
          <p className="text-sm text-gray-500 mt-2">
            <a href="#" className="text-ta-green hover:underline">Report changes</a>
          </p>
        </div>
      </section>

      {/* Booking Modal */}
      <NightlifeBookingForm 
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        selectedPackage={selectedPackage}
      />

      {/* Guide Modal */}
      <NightCrawlingGuideModal
        isOpen={isGuideModalOpen}
        onClose={() => setIsGuideModalOpen(false)}
      />

      {/* Terms & Conditions Modal */}
      <NightlifeTermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
        packageType={selectedTermsPackage}
      />

      <Footer />
    </>
  );
};

export default UgandaNightlifeGuide;